import {
    BrowserAuthErrorCodes,
    InteractionRequiredAuthErrorCodes,
    PublicClientApplication
} from '@azure/msal-browser';
import UiConfig from 'constants/ui-config';

const msalConfig = {
    auth: {
        clientId: 'd9a6c163-acf7-4dbb-881c-57edc7f8f40c',
        authority: 'https://login.microsoftonline.com/b223ef84-eb37-44e0-8ef9-6b666a35bdce',
        redirectUri: UiConfig.ENVIRONMENT === 'local'
            ? 'http://localhost:3001/redirect.html'
            : `${document.location.origin}/redirect.html`
    },
    cache: {
        cacheLocation: 'sessionStorage'
    },
    system: {
        // allowPlatformBroker flag can help prevent the browser from generating
        // a popup that asks the user to let the UI access other apps and
        // services on the user's device. Ineffective for Safari browser.
        allowPlatformBroker: false
    }
};

// PublicClientApplication is the recommended class for most SPAs. See:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/
// lib/msal-browser/docs/v4-migration.md#migration-example
const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// The following BrowserAuthError codes are the ones that would reasonably be
// possible for loginPopup() to throw in a browser environment. Codes at:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/
//      lib/msal-browser/src/error/BrowserAuthErrorCodes.ts
const BROWSER_AUTH_ERROR_MESSAGES = {
    // https://aka.ms/msal.js.errors#block_nested_popups
    [BrowserAuthErrorCodes.blockNestedPopups]: 'oh no, there is a popup in a popup',
    // https://aka.ms/msal.js.errors#uninitialized_public_client_application
    [BrowserAuthErrorCodes.uninitializedPublicClientApplication]: 'PublicClientApplication has not been initialized',
    // https://aka.ms/msal.js.errors#interaction_in_progress
    [BrowserAuthErrorCodes.interactionInProgress]: 'loginPopup() invoked while another interactive API is in progress',
    // https://aka.ms/msal.js.errors#empty_navigate_uri
    [BrowserAuthErrorCodes.emptyNavigateUri]: 'navigate URI is empty, check the stack trace',
    // https://aka.ms/msal.js.errors#empty_window_error
    [BrowserAuthErrorCodes.emptyWindowError]: 'browser has blocked the popup window or window.open() returned null',
    // https://aka.ms/msal.js.errors#popup_window_error
    // Notably, Safari browsers
    [BrowserAuthErrorCodes.popupWindowError]: 'an error occurred opening the popup window, which may happen on '
        + 'browsers that block popups',
    // https://aka.ms/msal.js.errors#timed_out
    [BrowserAuthErrorCodes.timedOut]: 'popup timed out waiting for a response (user may have closed it)',
    // https://aka.ms/msal.js.errors#interaction_in_progress_cancelled
    [BrowserAuthErrorCodes.interactionInProgressCancelled]: 'overrideInteractionInProgress is set to true',
    // https://aka.ms/msal.js.errors#redirect_bridge_empty_response
    [BrowserAuthErrorCodes.redirectBridgeEmptyResponse]: 'could not find redirect bridge (redirect.html)'
};

// InteractionRequiredAuthError codes:
// https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/
//      lib/msal-common/src/error/ClientConfigurationErrorCodes.ts
// For server-side error code documentation, see:
// https://learn.microsoft.com/en-us/entra/identity-platform/
//      reference-error-codes
const INTERACTION_REQUIRED_AUTH_ERROR_MESSAGES = {
    [InteractionRequiredAuthErrorCodes.noTokensFound]: 'no tokens were found in cache. This user has likely not signed '
        + 'in this browser',
    [InteractionRequiredAuthErrorCodes.refreshTokenExpired]: 'this returning user has an expired refresh token in '
        + 'cache',
    // interactionRequired: OIDC error
    [InteractionRequiredAuthErrorCodes.interactionRequired]: 'server-side error: this account needs to be added as an '
        + 'external user in the tenant first',
    // consentRequired: AADSTS65001 DelegationDoesNotExist
    [InteractionRequiredAuthErrorCodes.consentRequired]: 'server-side error: Entra admin has not consented to use this '
        + 'application',
    // loginRequired: OIDC error
    [InteractionRequiredAuthErrorCodes.loginRequired]: 'server-side error: login is required',
    // badToken: AADSTS9002313 InvalidRequest
    [InteractionRequiredAuthErrorCodes.badToken]: 'server-side error: the token is invalid or corrupted'
    // interruptedUser: couldn't find documentation
    // [InteractionRequiredAuthErrorCodes.interruptedUser]: ...
    // nativeAccountUnavailable: does not apply to msal-browser web flows
    // [InteractionRequiredAuthErrorCodes.nativeAccountUnavailable]: ...
};

export {
    BROWSER_AUTH_ERROR_MESSAGES,
    INTERACTION_REQUIRED_AUTH_ERROR_MESSAGES,
    msalInstance
};
